import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { PlanningRunningEntity } from 'src/entities/PlanningsRunning.entity';

export const PLANNINGS_RUNNINGS_QUERY_KEY = 'useQueryPlanningsRunnings';

const requestPath = (startOn: string, endOn: string) => `/api/v3/plannings/running?start_on=${startOn}&end_on=${endOn}`;

const getAll = (startOn: string, endOn: string) => async () => {
  const response: AxiosResponse<PlanningRunningEntity[]> = await axios.get(requestPath(startOn, endOn));

  return response.data;
};

export const useQueryPlanningsRunnings = (startOn: string, endOn: string) => useQuery(
  [PLANNINGS_RUNNINGS_QUERY_KEY, { startOn, endOn }],
  getAll(startOn, endOn),
  {
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(startOn) && Boolean(endOn)
  }
);

// 対象日付の running 一覧キャッシュを invalidate (再取得対象に) する。クエリキーの知識をこの
// フックファイル内にカプセル化し、呼び出し側がキー構造を知らなくて済むようにする。
// キャンセル失敗時 / ID 未取得の早期キャンセル / エラー時など、サーバ上でジョブがまだ実行中の
// 可能性がある場面で、楽観的に空へはせず実態を再取得させるために使う。
export const invalidateRunningPlanningsCache = (
  queryClient: QueryClient,
  startOn: string,
  endOn: string,
): Promise<void> =>
  queryClient.invalidateQueries({
    queryKey: [PLANNINGS_RUNNINGS_QUERY_KEY, { startOn, endOn }],
    exact: true,
  });

// 自動配車が終了 (完了 / キャンセル / エラー) した直後に、対象日付の running 一覧キャッシュを
// 即座に空へ上書きする。このキャッシュは地図画面 (V3Plans) や幹線輸送と
// ['useQueryPlanningsRunnings'] を共有しており、staleTime が長いため、クリアしないと完了後に
// 画面遷移した際「n分前から自動配車を実行しています」が一瞬残って表示されてしまう。
// invalidate (再取得) では取得完了までの間に古い値を見せてしまうため、終了が確定している時点で
// setQueriesData により楽観的に空配列へ置き換える。
//
// 対象は完了した自動配車の { startOn, endOn } のみに限定する (exact: true)。prefix 全体を消すと、
// 別日付や他画面で実行中の自動配車のバナー/停止導線まで staleTime の間消えてしまうため。
// また predicate でデータ既存のクエリのみを対象にし、未取得 (undefined) のクエリには値を作らず
// 不要な状態更新も行わない (初回フェッチを妨げないため)。
//
// 楽観的に空配列へ置き換えるだけだと、同一日付で別の自動配車 (幹線輸送や別画面のジョブ) がまだ
// 実行中の場合にそれも staleTime の間消えてしまう。そこで直後に同キーを invalidate し、実態を
// 再取得させる。これにより「フラッシュは [] で即時抑止しつつ、同一日付の他ジョブは refetch で復元」
// となる (他ジョブが非表示になるのは refetch 完了までの一瞬のみ)。
// 戻り値の Promise は invalidate (再取得) の完了を表す。呼び出し側やテストで完了を待ち合わせたい
// 場合に await できるよう Promise<void> を返す (setQueriesData による [] への置き換えは同期的に完了)。
export const clearRunningPlanningsCache = (
  queryClient: QueryClient,
  startOn: string,
  endOn: string,
): Promise<void> => {
  queryClient.setQueriesData<PlanningRunningEntity[]>(
    {
      queryKey: [PLANNINGS_RUNNINGS_QUERY_KEY, { startOn, endOn }],
      exact: true,
      predicate: (query) => query.state.data !== undefined,
    },
    () => [],
  );
  return invalidateRunningPlanningsCache(queryClient, startOn, endOn);
};

export const useMutationPlanningRunnings = () => {
  return {
    delete: useMutation(
      ({ id, startOn, endOn }: { id: number, startOn: string, endOn: string }) => axios.delete(
        '/api/v3/plannings/running',
        { data: { id, start_on: startOn, end_on: endOn } }
      ),
    ),
  };
};
